import { useApolloClient } from '@apollo/client';
import { Dashboard, PublicOutlined, Style, Work } from '@mui/icons-material';
import Business from '@mui/icons-material/Business';
import Chat from '@mui/icons-material/Chat';
import Email from '@mui/icons-material/Email';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import Person from '@mui/icons-material/Person';
import Place from '@mui/icons-material/Place';
import SportsIcon from '@mui/icons-material/SportsMartialArts';
import buildGraphQLProvider from 'ra-data-graphql';
import * as React from 'react';
import { Admin, CustomRoutes, Resource } from 'react-admin';
// Upgrading to latest RA causes a funny issue where we need to have ONLY react-router-dom in dependencies but import Route from react-router
import { Route } from 'react-router';
import buildQuery, { introspection } from '../../admin/buildQuery';
import ExosAuthProvider from '../../admin/ExosAuthProvider';
import {
  canWrite as userCanWrite,
  isAdmin as userIsAdmin,
} from '../../admin/permissions';
import { SplitFlags, useFeatureFlags } from '../../hooks/useFeatureFlags';
import useLogout from '../../hooks/useLogout';
import theme from '../../theme';
import { EnvLevel, getEnvLevel } from '../../utils/getEnvLevel';
import {
  ChallengeCreate,
  ChallengeEdit,
  ChallengeShow,
  ChallengesList,
} from './Challenges';
import { ChatRoomCreate, ChatRoomEdit, ChatRoomList } from './ChatRoom';
import { ClientCreate, ClientEdit, ClientList, ClientShow } from './Client';
import { ContentList, ContentShow } from './Content';
import { CustomLayout } from './CustomMenu';
import { DomainCreate, DomainEdit, DomainList, DomainShow } from './Domain';
import {
  EligibleEmailAddressCreate,
  EligibleEmailAddressEdit,
  EligibleEmailAddressList,
  EligibleEmailAddressShow,
} from './EligibleEmailAddress';
import { EmailDebugger } from './EmailDebugger';
import {
  ExerciseCreate,
  ExerciseEdit,
  ExerciseList,
  ExerciseShow,
} from './Exercise';
import { GenreCreate, GenreEdit, GenreList, GenreShow } from './Genre';
import {
  LocationCreate,
  LocationEdit,
  LocationList,
  LocationShow,
} from './Location';
import {
  MboContractMappingCreate,
  MboContractMappingEdit,
  MboContractMappingList,
  MboContractMappingShow,
} from './MboContractMapping';
import { OfficeCreate, OfficeEdit, OfficeList, OfficeShow } from './Office';
import { StudioCreate, StudioEdit, StudioList, StudioShow } from './Studio';
import { SystemPreferences } from './SystemPreferences';
import { TestData } from './TestData';
import { UserCreate, UserEdit, UserList, UserShow } from './User';

const AdminPage: React.FC = () => {
  const client = useApolloClient();
  const logout = useLogout();
  const { flags } = useFeatureFlags();
  const enableTestDataService = flags[SplitFlags.SPA_TEST_DATA_SERVICE];

  const [authProvider] = React.useState(
    () => new ExosAuthProvider(client, logout),
  );

  const dataProvider = buildGraphQLProvider({
    buildQuery,
    client,
    introspection,
  });

  if (!dataProvider) {
    return <div>Loading</div>;
  }

  return (
    <Admin
      basename="/app"
      theme={theme}
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={false}
      layout={CustomLayout}
    >
      {(permissions) => {
        const isAdmin = userIsAdmin(permissions);
        const canWrite = userCanWrite(permissions);
        return (
          <>
            {isAdmin && (
              <>
                {/* C L I E N T */}
                <Resource
                  name="Client"
                  options={{ label: 'Client' }}
                  list={ClientList}
                  create={canWrite ? ClientCreate : undefined}
                  edit={canWrite ? ClientEdit : undefined}
                  show={ClientShow}
                  icon={Business}
                  recordRepresentation={(record) => `#${record.id}`}
                />
                <Resource
                  name="Studio"
                  list={StudioList}
                  show={StudioShow}
                  edit={canWrite ? StudioEdit : undefined}
                  create={canWrite ? StudioCreate : undefined}
                  icon={FitnessCenter}
                />
                <Resource
                  name="MboContractMapping"
                  options={{ label: 'MBO Contract Mapping' }}
                  list={MboContractMappingList}
                  show={MboContractMappingShow}
                  edit={canWrite ? MboContractMappingEdit : undefined}
                  create={canWrite ? MboContractMappingCreate : undefined}
                />
                <Resource
                  name="CoachLocation"
                  list={LocationList}
                  show={LocationShow}
                  edit={canWrite ? LocationEdit : undefined}
                  create={canWrite ? LocationCreate : undefined}
                  options={{ label: 'Location' }}
                  icon={Place}
                />
                <Resource
                  name="Office"
                  list={OfficeList}
                  show={OfficeShow}
                  edit={canWrite ? OfficeEdit : undefined}
                  create={canWrite ? OfficeCreate : undefined}
                  icon={Work}
                />
                <Resource
                  name="Domain"
                  list={DomainList}
                  edit={canWrite ? DomainEdit : undefined}
                  show={DomainShow}
                  create={canWrite ? DomainCreate : undefined}
                  icon={PublicOutlined}
                />
                {/* U S E R */}
                <Resource
                  name="User"
                  options={{ label: 'User' }}
                  list={UserList}
                  create={canWrite ? UserCreate : undefined}
                  edit={canWrite ? UserEdit : undefined}
                  show={UserShow}
                  icon={Person}
                  recordRepresentation={(record) =>
                    `${record.firstName} ${record.lastName}`
                  }
                />
                <Resource
                  name="EligibleEmailAddress"
                  options={{ label: 'Eligible Email' }}
                  list={EligibleEmailAddressList}
                  show={EligibleEmailAddressShow}
                  edit={canWrite ? EligibleEmailAddressEdit : undefined}
                  create={canWrite ? EligibleEmailAddressCreate : undefined}
                  icon={Email}
                />
                <Resource
                  name="ChatRoom"
                  options={{ label: 'Chat Room' }}
                  list={ChatRoomList}
                  create={canWrite ? ChatRoomCreate : undefined}
                  edit={canWrite ? ChatRoomEdit : undefined}
                  icon={Chat}
                />

                {/* C O N T E N T */}
                <Resource
                  name="Content"
                  list={ContentList}
                  show={ContentShow}
                  icon={Dashboard}
                />
                <Resource
                  name="Genre"
                  list={GenreList}
                  show={GenreShow}
                  edit={canWrite ? GenreEdit : undefined}
                  create={canWrite ? GenreCreate : undefined}
                  icon={Style}
                  options={{ label: 'Genre' }}
                />
                <Resource
                  name="Challenge"
                  list={ChallengesList}
                  show={ChallengeShow}
                  edit={canWrite ? ChallengeEdit : undefined}
                  create={canWrite ? ChallengeCreate : undefined}
                  icon={EmojiEvents}
                />
                <Resource
                  options={{ label: 'Movements & Activities' }}
                  name="Exercise"
                  list={ExerciseList}
                  show={ExerciseShow}
                  edit={canWrite ? ExerciseEdit : undefined}
                  create={canWrite ? ExerciseCreate : undefined}
                  icon={SportsIcon}
                />
                <CustomRoutes>
                  {(getEnvLevel() === EnvLevel.DEV ||
                    getEnvLevel() === EnvLevel.TESTING) &&
                    enableTestDataService && (
                      <Route path="/testData" element={<TestData />} />
                    )}
                  <Route path="/emailDebugger" element={<EmailDebugger />} />
                  <Route
                    path="/systemPreferences"
                    element={<SystemPreferences />}
                  />
                </CustomRoutes>
              </>
            )}
            <Resource
              options={{ label: 'Movements & Activities' }}
              name="Exercise"
              list={ExerciseList}
              create={ExerciseCreate}
              edit={ExerciseEdit}
              show={ExerciseShow}
              icon={SportsIcon}
            />
          </>
        );
      }}
    </Admin>
  );
};

export default AdminPage;
